import React, { useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { ExportAplPeriodToExcel } from "../../actions/teacherMutations";
import { compareByName } from "../../Utilities/sortHelper";

const EXPORT_FILTERS = [
  { id: 1, messageId: "FilterAttendance" },
  { id: 2, messageId: "FilterWorkcard" },
  { id: 3, messageId: "FilterWorkcardFlow" },
  { id: 4, messageId: "FilterAdministerPeriod" },
];

const AplExportModal = ({
  isOpen,
  toggle,
  periodId,
  periodStudents,
  contextStudents,
}) => {
  const intl = useIntl();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useMutation({ mutationFn: ExportAplPeriodToExcel });

  const eligibleStudents = useMemo(() => {
    if (!periodStudents || !contextStudents) return [];
    const contextStudentIds = new Set(contextStudents.map((s) => s.id));
    return periodStudents
      .filter((ps) => contextStudentIds.has(ps.student.id))
      .map((ps) => ps.student)
      .sort(compareByName);
  }, [periodStudents, contextStudents]);

  const handleFilterToggle = (filterId) => {
    setSelectedFilters((prev) =>
      prev.includes(filterId)
        ? prev.filter((f) => f !== filterId)
        : [...prev, filterId]
    );
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAllStudents = () => {
    if (selectedStudentIds.length === eligibleStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(eligibleStudents.map((s) => s.id));
    }
  };

  const handleExport = () => {
    if (selectedFilters.length === 0) {
      toast.warn(intl.formatMessage({ id: "SelectAtLeastOneFilter" }));
      return;
    }
    if (selectedStudentIds.length === 0) {
      toast.warn(intl.formatMessage({ id: "SelectAtLeastOneStudent" }));
      return;
    }

    setIsExporting(true);

    const exportRequest = {
      PeriodId: periodId,
      StudentIds: selectedStudentIds,
      Filters: selectedFilters,
    };

    exportMutation.mutate(exportRequest, {
      onSuccess: (response) => {
        if (response && response.data) {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const filename = `APL_Export_${periodId}_${
            new Date().toISOString().split("T")[0]
          }.xlsx`;
          saveAs(blob, filename);
          toast.success(intl.formatMessage({ id: "ExportSuccessful" }));
          handleClose();
        }
        setIsExporting(false);
      },
      onError: async (error) => {
        setIsExporting(false);
        if (error.response?.data instanceof Blob) {
          const errorText = await error.response.data.text();
          toast.warn(errorText || intl.formatMessage({ id: "GenericApiError" }));
        } else {
          toast.warn(
            error.response?.data || intl.formatMessage({ id: "GenericApiError" })
          );
        }
      },
    });
  };

  const handleClose = () => {
    setSelectedFilters([]);
    setSelectedStudentIds([]);
    setIsExporting(false);
    toggle();
  };

  const allStudentsSelected =
    eligibleStudents.length > 0 &&
    selectedStudentIds.length === eligibleStudents.length;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        <FormattedMessage id="ExportToExcel" />
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <h6>
              <FormattedMessage id="SelectFiltersForExport" />
            </h6>
            {EXPORT_FILTERS.map((filter) => (
              <FormGroup check key={filter.id}>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={selectedFilters.includes(filter.id)}
                    onChange={() => handleFilterToggle(filter.id)}
                  />
                  <FormattedMessage id={filter.messageId} />
                </Label>
              </FormGroup>
            ))}
          </Col>
          <Col md={6}>
            <h6>
              <FormattedMessage id="SelectStudentsForExport" />
            </h6>
            <FormGroup check className="mb-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={allStudentsSelected}
                  onChange={handleSelectAllStudents}
                />
                <strong>
                  <FormattedMessage
                    id={allStudentsSelected ? "DeselectAll" : "SelectAll"}
                  />
                </strong>
              </Label>
            </FormGroup>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {eligibleStudents.map((student) => (
                <FormGroup check key={student.id}>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={selectedStudentIds.includes(student.id)}
                      onChange={() => handleStudentToggle(student.id)}
                    />
                    {student.name}
                  </Label>
                </FormGroup>
              ))}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button outline color="secondary" onClick={handleClose}>
          <FormattedMessage id="Cancel" />
        </Button>
        <Button
          color="primary"
          onClick={handleExport}
          disabled={
            isExporting ||
            selectedFilters.length === 0 ||
            selectedStudentIds.length === 0
          }
        >
          {isExporting ? "..." : <FormattedMessage id="GetReport" />}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AplExportModal;
